import type { stacks } from "@dropbox/api-v2-client";
import { TextArea } from "@dropbox/dig-components/text_fields";
import { Title } from "@dropbox/dig-components/typography";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Cancel_StacksName } from "@mirage/analytics/events/types/cancel_stacks_name";
import { PAP_Click_StacksName } from "@mirage/analytics/events/types/click_stacks_name";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { dispatchElementClicked } from "@mirage/analytics/uxa/dispatchElementClicked";
import { MAX_STACK_NAME_LENGTH } from "@mirage/shared/util/constants";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { StackDetailsTourTooltip } from "../FullScreenStack/StackDetailsTourTooltip";
import { useUpdateStackName } from "../hooks";
import styles from "./StackNameEditor.module.css";

export const StackNameEditor = ({
  canEdit,
  editing,
  mutationRequestId,
  onEditStatusUpdate,
  sessionId,
  stack,
}: {
  canEdit?: boolean;
  editing?: boolean;
  mutationRequestId?: string;
  onEditStatusUpdate?: (status: boolean) => void;
  sessionId?: string;
  stack: stacks.Stack | null;
}) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const updateStackName = useUpdateStackName(
    stack,
    !!canEdit,
    mutationRequestId,
    sessionId,
  );

  const isNewStack = stack === null;
  let initialName = stack?.stack_data?.name || "";
  if (isNewStack) {
    // Prefer navigation state, then search params, then empty string
    const stateName = (location.state && location.state.name) || "";
    const paramName = searchParams.get("name") || "";
    initialName = stateName || paramName || "";
  }
  const [isEditing, setIsEditing] = useState(editing || isNewStack);
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  // Update name when the stack changes, but not while editing.
  useEffect(() => {
    if (!isEditing) {
      setName(initialName);
    }
  }, [initialName, isEditing]);

  const handleStartEditing = () => {
    if (!canEdit) return;
    setIsEditing(true);
    onEditStatusUpdate?.(true);
    const papProps = stack;
    reportPapEvent(
      PAP_Click_StacksName({
        featureLine: "stacks",
        ...(papProps ? papProps : {}),
      }),
    );
    dispatchElementClicked(
      createUxaElementId("stack_title", { featureLine: "stacks" }),
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
    setName(initialName);
    onEditStatusUpdate?.(false);
    const papProps = stack;
    reportPapEvent(
      PAP_Cancel_StacksName({
        featureLine: "stacks",
        ...(papProps ? papProps : {}),
      }),
    );
  };

  const handleSaveAndExit = async () => {
    if (name.trim() !== initialName.trim()) {
      await updateStackName(name);
    }
    setIsEditing(false);
    onEditStatusUpdate?.(false);
  };

  return (
    <div
      className={classnames(
        styles.stackTitleContainer,
        meticulousRedactedStringClass,
        { [styles.stackTitleContainerEdit]: isEditing },
      )}
    >
      {isEditing ? (
        <>
          <TextArea
            aria-label={i18n.t("edit_stack_title")}
            className={styles.stackNameInput}
            maxLength={MAX_STACK_NAME_LENGTH}
            onBlur={handleSaveAndExit}
            onChange={(e) => setName((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSaveAndExit();
              } else if (e.key === "Escape") {
                handleCancel();
              }
            }}
            placeholder={i18n.t("stack_title_placeholder")}
            ref={inputRef}
            rows={1}
            resizable="auto"
            value={name}
          />
          {canEdit && (
            <StackDetailsTourTooltip
              stepKey="name-stack"
              triggerRef={inputRef}
            />
          )}
        </>
      ) : (
        <>
          <div ref={titleRef}>
            <Title
              aria-label={i18n.t("edit_stack_title")}
              className={styles.stackName}
              onClick={handleStartEditing}
              onKeyDown={(e) => {
                if (canEdit && (e.key === "Enter" || e.key === " ")) {
                  handleStartEditing();
                }
              }}
              role={canEdit ? "button" : undefined}
              size="large"
              tabIndex={canEdit ? 0 : undefined}
              weightVariant="emphasized"
            >
              {name || i18n.t("untitled_stack")}
            </Title>
          </div>
          {canEdit && (
            <StackDetailsTourTooltip
              stepKey="name-stack"
              triggerRef={titleRef}
            />
          )}
        </>
      )}
    </div>
  );
};
